"use client";

import type { StaticImport } from "next/dist/shared/lib/get-img-props";
/* eslint-disable no-restricted-imports */
import NextImage, {
  type ImageLoader,
  type ImageProps as NextImageProps,
} from "next/image";
import { useState } from "react";

import { isCloudinaryUrl } from "@/lib/cloudinary/url";
import { cn } from "@/lib/utils";

// =============================================================================

/**
 * Cloudinary resizes and converts its own images (f_auto picks AVIF/WebP):
 * this keeps them out of the Vercel image optimization quota.
 */
const cloudinaryLoader: ImageLoader = ({ src, width, quality }) => {
  const transformations = [
    "f_auto",
    `q_${quality ?? "auto"}`,
    "c_limit",
    `w_${width}`,
  ].join(",");
  return src.replace("/upload/", `/upload/${transformations}/`);
};

// =============================================================================

export type ImageProps = Omit<NextImageProps, "src"> & {
  src?: string | StaticImport | null;
  /** Rendered when there is no src or the image failed to load */
  fallback?: React.ReactNode;
  /** Class of the pulsing placeholder shown while loading */
  loadingClassName?: string;
};

/**
 * next/image wrapper (same idea as the Hub's Image component): loading pulse,
 * error fallback and automatic Cloudinary loader.
 */
export function Image({
  src,
  fallback = null,
  className,
  loadingClassName,
  onLoad,
  onError,
  ...props
}: ImageProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) return <>{fallback}</>;

  const loader =
    typeof src === "string" && isCloudinaryUrl(src)
      ? cloudinaryLoader
      : undefined;

  return (
    <NextImage
      {...props}
      src={src}
      loader={loader}
      className={cn(
        className,
        isLoading && cn("animate-pulse bg-surface-2", loadingClassName)
      )}
      onLoad={(event) => {
        setIsLoading(false);
        onLoad?.(event);
      }}
      onError={(event) => {
        setHasError(true);
        onError?.(event);
      }}
    />
  );
}

// =============================================================================
